/**
 * InstCodingRoute — candidate-facing coding test for institutional campaigns.
 * URL: /inst/coding/:campaignId
 *
 * - Loads N random problems from the backend (no auth needed for problem fetch)
 * - Inline Firebase auth if not logged in
 * - Multi-problem editor with Monaco, one problem at a time
 * - Run via Judge0 (/coding/run), submit all at end
 * - Results stored in coding_campaign_submissions with campaign_id
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";
import { getAuthToken } from "./api";
import toast from "react-hot-toast";
import useCampaignProctoring from "./hooks/useCampaignProctoring";
import useSubmissionSafety from "./hooks/useSubmissionSafety";
import useGradingJob, { gradingMessage } from "./hooks/useGradingJob";
import { isScreenShareSupported } from "./utils/proctoringSupport";
import "./InstitutionalDashboard.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const LANGUAGES = [
  { value: "python",     label: "Python",     monacoLang: "python",     judge0Id: 71 },
  { value: "java",       label: "Java",       monacoLang: "java",       judge0Id: 62 },
  { value: "javascript", label: "JavaScript", monacoLang: "javascript", judge0Id: 63 },
  { value: "c",          label: "C",          monacoLang: "c",          judge0Id: 50 },
  { value: "cpp",        label: "C++",        monacoLang: "cpp",        judge0Id: 54 },
  { value: "csharp",     label: "C#",         monacoLang: "csharp",     judge0Id: 51 },
  { value: "ruby",       label: "Ruby",       monacoLang: "ruby",       judge0Id: 72 },
  { value: "swift",      label: "Swift",      monacoLang: "swift",     judge0Id: 83 },
];

const STARTERS = {
  python:     "# Write your solution here\n",
  java:       "class Main {\n  public static void main(String[] args) {\n    // Write your solution here\n  }\n}\n",
  javascript: "// Write your solution here\n",
  cpp:        "#include <bits/stdc++.h>\nusing namespace std;\n\nint main() {\n  // Write your solution here\n  return 0;\n}\n",
  csharp:     "using System;\n\nclass Program {\n  static void Main() {\n    // Write your solution here\n  }\n}\n",
  ruby:       "# Write your solution here\n",
  swift:      "import Foundation\n\n// Write your solution here\n",
  c:          "#include <stdio.h>\n\nint main() {\n  // Write your solution here\n  return 0;\n}\n",
};

function getStarter(problem, lang) {
  const sc = problem?.starter_code || {};
  return sc[lang] || STARTERS[lang] || "";
}

// ─── Self-view camera overlay (identical to InstMcqRoute) ────────────────────

function SelfViewCamera({ onStream, onStatus }) {
  const videoRef = useRef(null);
  const [status, setStatus] = useState("requesting");

  useEffect(() => {
    let stream = null;
    navigator.mediaDevices
      .getUserMedia({ video: { width: 160, height: 120, facingMode: "user" }, audio: false })
      .then((s) => {
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play().catch(() => {});
        }
        setStatus("active");
        onStream?.(s);
        onStatus?.("active");
      })
      .catch(() => { setStatus("denied"); onStatus?.("denied"); });
    return () => { stream?.getTracks().forEach((t) => t.stop()); };
  }, []); // eslint-disable-line

  if (status === "denied") {
    return (
      <div className="inst-selfview inst-selfview--denied">
        <span>📷</span>
        <p>Camera denied</p>
      </div>
    );
  }
  return (
    <div className="inst-selfview">
      <video ref={videoRef} className="inst-selfview__video" muted playsInline autoPlay />
      <div className="inst-selfview__label">
        <span className="inst-selfview__dot" />
        Proctored
      </div>
    </div>
  );
}

// ─── Campaign top bar ─────────────────────────────────────────────────────────

function CampaignTopBar({ integrityScore, integrityLevel }) {
  const navigate = useNavigate();
  const handleLogout = async () => {
    await signOut(auth).catch(() => {});
    navigate("/login");
  };
  return (
    <div className="inst-topbar">
      <div className="inst-topbar__brand">
        <img src="/Vagmee_Logo.png" alt="Vagmee" className="inst-topbar__logo" />
        <span className="inst-topbar__name">Vagmee</span>
      </div>
      <div className="inst-topbar__right">
        {integrityScore !== undefined && (
          <span className={`inst-topbar__integrity lvl-${integrityLevel}`}>
            ◉ Integrity {integrityScore}
          </span>
        )}
        <button className="inst-topbar__logout" onClick={handleLogout}>Logout</button>
      </div>
    </div>
  );
}

// ─── Submitting overlay ───────────────────────────────────────────────────────

function SubmittingOverlay({ step }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => (t + 1) % 4), 500);
    return () => clearInterval(id);
  }, []);
  const dots = ".".repeat(tick);
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(8,4,14,0.9)", backdropFilter: "blur(6px)",
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", gap: "1rem", padding: "2rem", textAlign: "center",
    }}>
      <div style={{ fontSize: "2.5rem", lineHeight: 1 }}>
        {step === "retrying" ? "🔄" : step === "verifying" ? "✅" : "⏳"}
      </div>
      <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 700, color: "rgba(255,255,255,0.92)" }}>
        {step === "retrying"  ? "Connection issue — retrying automatically…" :
         step === "verifying" ? "Verifying your submission…"                :
         `Submitting Coding Assessment${dots}`}
      </h2>
      <p style={{ margin: 0, color: "rgba(255,255,255,0.5)", fontSize: "0.88rem" }}>
        Please do not close this tab.
      </p>
      {step === "submitting" && (
        <p style={{ margin: 0, color: "rgba(255,255,255,0.28)", fontSize: "0.78rem", maxWidth: 320 }}>
          This may take up to 30 seconds during peak usage.
        </p>
      )}
      {step === "retrying" && (
        <p style={{ margin: 0, color: "#f6c05a", fontSize: "0.82rem" }}>
          Retrying in 3 seconds…
        </p>
      )}
    </div>
  );
}

// ─── Coding test UI ───────────────────────────────────────────────────────────

export function CodingTest({
  campaign, problems, onSubmitAll, mcqScore = null, campaignId = null, deadlineEpoch = null,
  runEndpoint = `${API_BASE}/api/institutional/coding/run`,
  statusEndpoint = campaignId ? `${API_BASE}/api/institutional/campaigns/${campaignId}/my-status` : null,
  hideScore = false,
  hideSubmitUntilLast = false,
  // Institutional's combined MCQ+Coding route already shows a global timer
  // in its own top bar (same deadlineEpoch) — set this so this component
  // doesn't render a redundant second countdown in that one context.
  hideInternalTimer = false,
  // Epoch ms of the last time a recruiter granted this candidate a retake
  // (null if never). A saved draft older than this is from BEFORE the
  // retake — i.e. the previous, already-scored attempt — and must not be
  // restored into what's supposed to be a fresh test.
  minDraftTimestamp = null,
}) {
  const draftKey     = campaignId ? `vagmee_coding_${campaignId}`           : null;
  const statusKey    = campaignId ? `vagmee_coding_status_${campaignId}`    : null;
  const emergencyKey = campaignId ? `vagmee_coding_emergency_${campaignId}` : null;

  const [current, setCurrent] = useState(0);
  // Tracked per-problem, not as one flat value — a candidate can reasonably
  // write different problems in different languages (or just leave the
  // dropdown on whatever they last picked while browsing other problems).
  // A single shared `lang` used to mean "Submit All" tagged EVERY problem's
  // submission with whichever language happened to be selected at the
  // moment of submitting, regardless of what language each problem's code
  // was actually written in — silently failing correct solutions that
  // didn't happen to match the last-selected language.
  // Defaults to whatever language the recruiter wrote the reference solution
  // in (problem.reference_language), not always Python — a candidate facing
  // a Java-only reference solution shouldn't have to notice and manually
  // switch away from a Python default before they can even start. Problems
  // that don't carry a reference_language at all (e.g. institutional
  // campaign problems, which predate this field) fall back to Python,
  // preserving existing behavior there.
  const [langs, setLangs] = useState(() =>
    Object.fromEntries(problems.map((p, i) => [i, p.reference_language || "python"]))
  );
  const lang = langs[current] ?? "python";
  const [codes, setCodes] = useState(() =>
    Object.fromEntries(problems.map((p, i) => [i, getStarter(p, p.reference_language || "python")]))
  );
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [gradingJobId, setGradingJobId] = useState(null);
  const grading = useGradingJob(gradingJobId);
  const [submitting, setSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState(null); // null|'submitting'|'retrying'|'verifying'
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState("draft");
  const [showMissedConfirm, setShowMissedConfirm] = useState(false);
  const { isOnline } = useSubmissionSafety();

  const submitLock      = useRef(false);
  const submitStartRef  = useRef(null);
  const retryCountRef   = useRef(0);
  const saveTimerRef    = useRef(null);
  const queuedSub       = useRef(null); // holds submissions when offline

  const problem = problems[current];
  const langObj = LANGUAGES.find((l) => l.value === lang) || LANGUAGES[0];

  // ── Persistence helpers ────────────────────────────────────────────────────

  const saveDraft = useCallback((codesSnap, langsSnap, currentSnap) => {
    if (!draftKey || submitted) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        codes: codesSnap, langs: langsSnap, current: currentSnap, savedAt: Date.now(),
        retakeGeneration: minDraftTimestamp || null,
      }));
      setLastSavedAt(Date.now());
    } catch (_) {}
  }, [draftKey, submitted, minDraftTimestamp]);

  const saveStatus = useCallback((status) => {
    setSubmissionStatus(status);
    if (statusKey) try { localStorage.setItem(statusKey, status); } catch (_) {}
  }, [statusKey]);

  // ── Restore draft on mount ─────────────────────────────────────────────────

  useEffect(() => {
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const { codes: saved, langs: savedLangs, lang: savedLangLegacy, current: savedCurrent, savedAt, retakeGeneration } = JSON.parse(raw);
      // Comparing the draft's OWN recorded retake generation, not just
      // savedAt — a candidate whose browser tab stayed open (e.g. the
      // "dropped connection mid-test" case retakes exist for) keeps
      // autosaving the SAME stale code every 5s, which pushes savedAt
      // forward even though nothing new was actually written. retakeGeneration
      // is stamped once per attempt and isn't affected by that.
      if ((retakeGeneration || null) !== (minDraftTimestamp || null)) {
        // Either from before this retake was granted, or (a draft saved by
        // an older build that never recorded a generation) unverifiable —
        // discard rather than risk restoring previous, already-scored code.
        localStorage.removeItem(draftKey);
        if (emergencyKey) localStorage.removeItem(emergencyKey);
        return;
      }
      if (!saved || Date.now() - savedAt > 86400000) return;
      let restored = false;
      // savedLangs is the current per-problem shape; savedLangLegacy is a
      // fallback for drafts saved before this fix (a single flat language),
      // applied to every problem as a best-effort default on restore.
      if (savedLangs && typeof savedLangs === "object") {
        setLangs((prev) => ({ ...prev, ...savedLangs }));
      } else if (savedLangLegacy) {
        setLangs((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, savedLangLegacy])));
      }
      if (typeof savedCurrent === "number" && savedCurrent >= 0 && savedCurrent < problems.length) {
        setCurrent(savedCurrent);
      }
      if (Object.keys(saved).length > 0) {
        setCodes(prev => {
          const merged = { ...prev };
          Object.entries(saved).forEach(([k, v]) => {
            const idx = Number(k);
            const langForIdx = (savedLangs && savedLangs[idx]) || savedLangLegacy || "python";
            if (v && v.trim() !== getStarter(problems[idx], langForIdx).trim()) {
              merged[idx] = v;
              restored = true;
            }
          });
          return merged;
        });
        if (restored) {
          saveStatus("recovered");
          console.log(`[CodingTest] draft_restored campaign=${campaignId}`);
          toast("Coding draft restored from your last session.", { icon: "📋", duration: 4000 });
        }
      }
      // Check for queued emergency submission
      if (emergencyKey) {
        const em = localStorage.getItem(emergencyKey);
        if (em) toast("A previous submission attempt is stored. Tap 'Submit All' to re-send it.", { icon: "⚠️", duration: 6000 });
      }
    } catch (_) {}
  }, []); // eslint-disable-line

  // ── Auto-save every 5 seconds ─────────────────────────────────────────────

  useEffect(() => {
    if (!draftKey || submitted) return;
    const id = setInterval(() => saveDraft(codes, langs, current), 5000);
    return () => clearInterval(id);
  }, [codes, langs, current, draftKey, submitted, saveDraft]);

  // ── Debounced save on editor change (1s after last keystroke) ─────────────

  const setCode = (val) => {
    const newCodes = { ...codes, [current]: val ?? "" };
    setCodes(newCodes);
    if (draftKey && !submitted) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        try {
          localStorage.setItem(draftKey, JSON.stringify({
            codes: newCodes, langs, current, savedAt: Date.now(),
            retakeGeneration: minDraftTimestamp || null,
          }));
          setLastSavedAt(Date.now());
        } catch (_) {}
      }, 1000);
    }
  };

  const changeLang = (newLang) => {
    const newCodes = {
      ...codes,
      [current]: codes[current] === getStarter(problem, lang) || !codes[current]
        ? getStarter(problem, newLang)
        : codes[current],
    };
    const newLangs = { ...langs, [current]: newLang };
    setLangs(newLangs);
    setCodes(newCodes);
    if (draftKey && !submitted) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        try {
          localStorage.setItem(draftKey, JSON.stringify({
            codes: newCodes, langs: newLangs, current, savedAt: Date.now(),
            retakeGeneration: minDraftTimestamp || null,
          }));
          setLastSavedAt(Date.now());
        } catch (_) {}
      }, 500);
    }
  };

  // ── Flush queue when back online ──────────────────────────────────────────

  useEffect(() => {
    if (!isOnline || !queuedSub.current) return;
    const queued = queuedSub.current;
    queuedSub.current = null;
    toast("Connection restored — submitting your code now…", { icon: "🔄", duration: 4000 });
    _executeSubmit(queued, 0); // eslint-disable-line
  }, [isOnline]); // eslint-disable-line

  // ── Global deadline: auto-submit when the shared test-wide limit is reached ──
  // (For institutional campaigns this is a fixed 65-minute clock; for company
  // jobs it's whatever the recruiter set as the job's total test time.)

  useEffect(() => {
    if (!deadlineEpoch || submitted) return;
    if (Date.now() >= deadlineEpoch) { handleSubmitAll(); return; }
    const id = setInterval(() => {
      if (Date.now() >= deadlineEpoch) handleSubmitAll();
    }, 1000);
    return () => clearInterval(id);
  }, [deadlineEpoch, submitted]); // eslint-disable-line

  // ── Visible countdown for the same deadline ───────────────────────────────
  // This is the one place in the whole app that previously showed NO timer at
  // all during the coding round — candidates had no way to see how much time
  // was left, even though a hard deadline was silently enforced above.
  const [timeLeft, setTimeLeft] = useState(() =>
    deadlineEpoch ? Math.max(0, Math.floor((deadlineEpoch - Date.now()) / 1000)) : null
  );
  useEffect(() => {
    if (!deadlineEpoch) { setTimeLeft(null); return; }
    setTimeLeft(Math.max(0, Math.floor((deadlineEpoch - Date.now()) / 1000)));
    const id = setInterval(() => {
      setTimeLeft(Math.max(0, Math.floor((deadlineEpoch - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [deadlineEpoch]);
  const fmtTimeLeft = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const runCode = async () => {
    setRunning(true);
    setResults((r) => ({ ...r, [current]: { status: "running", output: "", error: "" } }));
    try {
      const token = await getAuthToken();

      // Use the first visible test case for stdin + expected_output so Judge0
      // can compare actual output. Without expected_output, Judge0 returns
      // status 3 ("Accepted") for any code that runs — even the starter stub.
      const tc = problem.visible_testcases?.[0];
      const stdin = tc ? (tc.input ?? tc.stdin ?? "") : "";
      const expected_output = tc ? (tc.expected_output ?? tc.output ?? "") : "";

      const payload = { source_code: codes[current], language_id: langObj.judge0Id };
      if (stdin) payload.stdin = stdin;
      if (expected_output) payload.expected_output = expected_output;

      const res = await fetch(runEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      const stdout = data.stdout || "";
      const stderr = data.compile_output || data.stderr || "";
      const statusObj = data.status || {};
      const statusId = typeof statusObj === "object" ? (statusObj.id || 0) : 0;
      const statusDesc = typeof statusObj === "object"
        ? (statusObj.description || "")
        : (typeof statusObj === "string" ? statusObj : "");

      // Judge0 status IDs:
      // 3 = Accepted  (output matches expected_output, or ran OK when no expected set)
      // 4 = Wrong Answer  (output doesn't match expected_output)
      // 5 = Time Limit Exceeded  6 = Compilation Error  etc.
      // Only treat status 3 as a genuine pass — never accept on statusDesc alone.
      const accepted = statusId === 3;

      setResults((r) => ({
        ...r,
        [current]: {
          status: accepted ? "accepted" : "error",
          output: stdout,
          error: stderr,
          statusDesc: statusDesc || (accepted ? "Accepted" : "Error"),
        },
      }));
    } catch (ex) {
      setResults((r) => ({
        ...r,
        [current]: { status: "error", output: "", error: ex.message, statusDesc: "Error" },
      }));
    } finally {
      setRunning(false);
    }
  };

  const _executeSubmit = async (submissions, attempt) => {
    retryCountRef.current = attempt;
    setSubmitStep(attempt === 0 ? "submitting" : "retrying");
    try {
      const receipt = await onSubmitAll(submissions);
      setGradingJobId(receipt?.grading_job_id || null);

      // Backend verification — poll /my-status up to 6×5s = 30s
      setSubmitStep("verifying");
      let verified = Boolean(receipt?.submission_id && receipt?.grading_job_id);
      if (statusEndpoint && !verified) {
        for (let i = 0; i < 6 && !verified; i++) {
          await new Promise(r => setTimeout(r, 5000));
          try {
            const token = await getAuthToken();
            const res = await fetch(
              statusEndpoint,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (res.ok && (await res.json()).attempted) verified = true;
          } catch (_) {}
        }
        if (!verified) console.warn("[CodingTest] backend_verification_timeout — showing success anyway");
      }

      // Success
      const ms = Date.now() - submitStartRef.current;
      console.log(`[CodingTest] submission_completed duration_ms=${ms} retry=${attempt}`);
      try { if (draftKey) localStorage.removeItem(draftKey); } catch (_) {}
      try { if (emergencyKey) localStorage.removeItem(emergencyKey); } catch (_) {}
      saveStatus("submitted");
      submitLock.current = false;
      setSubmitStep(null);
      setSubmitting(false);
      setSubmitted(true);

    } catch (err) {
      if (attempt === 0) {
        // Auto-retry once after 3s
        console.warn("[CodingTest] submission_failed attempt=0 — retrying in 3s");
        setSubmitStep("retrying");
        await new Promise(r => setTimeout(r, 3000));
        return _executeSubmit(submissions, 1);
      }
      // Final failure — emergency save
      try { if (emergencyKey) localStorage.setItem(emergencyKey, JSON.stringify({ submissions, savedAt: Date.now() })); } catch (_) {}
      saveStatus("failed");
      const ms = Date.now() - submitStartRef.current;
      console.error(`[CodingTest] submission_failed_final duration_ms=${ms}`);
      submitLock.current = false;
      setSubmitting(false);
      setSubmitStep(null);
      toast.error(
        "We could not verify your submission yet. Your code is saved safely — tap 'Submit All' to retry.",
        { duration: 10000 },
      );
    }
  };

  const handleSubmitAll = () => {
    if (submitLock.current) return; // prevent duplicate clicks
    submitLock.current = true;
    submitStartRef.current = Date.now();
    retryCountRef.current = 0;

    const submissions = problems.map((p, i) => ({
      problem_id: p.problem_id,
      title:      p.title,
      difficulty: p.difficulty,
      // Each problem's OWN language, not whatever happens to be selected for
      // the problem the candidate is currently viewing — see the note by
      // the langs state declaration for why this used to silently fail
      // correct solutions written in an earlier-selected language.
      language:   langs[i] ?? "python",
      code:       codes[i] || "",
      status:     results[i]?.status || "not_run",
    }));

    // Flush latest state to localStorage before submitting
    saveDraft(codes, langs, current);
    saveStatus("submitting");
    setSubmitting(true);

    if (!isOnline) {
      // Queue for when connection returns
      queuedSub.current = submissions;
      submitLock.current = false;
      setSubmitting(false);
      saveStatus("draft");
      toast("No internet connection. Your code is saved and will be submitted when you reconnect.", {
        icon: "📵", duration: 6000,
      });
      return;
    }

    _executeSubmit(submissions, 0);
  };

  // A problem counts as "missed" if its code is still exactly the starter
  // template (or blank) — i.e. the candidate never actually attempted it,
  // as opposed to attempting it and getting it wrong, which isn't "missed".
  // Only the manual Submit button goes through this check; auto-submit on
  // deadline expiry calls handleSubmitAll directly — there's no one to
  // confirm with once time is up.
  //
  // This used to be a window.confirm(), but that's a native browser dialog —
  // showing one forces an exit from fullscreen as an unavoidable side
  // effect, and re-requesting it afterward only works within a fresh user
  // gesture, which isn't guaranteed once a candidate takes a few seconds to
  // read the dialog and click OK. A custom, in-page confirmation is just DOM
  // content, so it never touches fullscreen at all.
  const missedCount = problems.filter((p, i) => {
    const code = (codes[i] || "").trim();
    const starter = getStarter(p, langs[i] ?? "python").trim();
    return !code || code === starter;
  }).length;
  const handleSubmitClick = () => {
    if (missedCount > 0) {
      setShowMissedConfirm(true);
      return;
    }
    handleSubmitAll();
  };
  const confirmSubmitAnyway = () => {
    setShowMissedConfirm(false);
    handleSubmitAll();
  };

  if (submitted) {
    const solved = gradingJobId
      ? (grading?.submissions || []).filter(item => item.status === "accepted").length
      : problems.filter((_, i) => results[i]?.status === "accepted").length;
    const isCombined = mcqScore !== null;
    return (
      <div className="instc-done">
        <div className="instc-done-icon">✓</div>
        <h2>Assessment Complete</h2>
        {hideScore || (gradingJobId && grading?.grading_status !== 'completed') ? (
          <p>Your responses have been submitted for review.</p>
        ) : isCombined ? (
          <div className="instc-done-scores">
            <div className="instc-done-score-card">
              <div className="instc-done-score-label">MCQ Score</div>
              <div className="instc-done-score-value">{mcqScore.scorePercent}%</div>
              <div className="instc-done-score-sub">{mcqScore.totalCorrect} / {mcqScore.totalQuestions} correct</div>
            </div>
            <div className="instc-done-score-card">
              <div className="instc-done-score-label">Coding</div>
              <div className="instc-done-score-value">{solved}/{problems.length}</div>
              <div className="instc-done-score-sub">problems solved</div>
            </div>
          </div>
        ) : (
          <p>{solved} / {problems.length} problems accepted</p>
        )}
        {gradingJobId && <p role="status" aria-live="polite">{gradingMessage(grading?.grading_status)}</p>}
        <p className="instc-done-sub">Your responses have been recorded. You may close this tab.</p>
      </div>
    );
  }

  const diffColor = { EASY: "#36c8a0", MEDIUM: "#f6c05a", HARD: "#f87171" };

  return (
    <>
    {submitting && <SubmittingOverlay step={submitStep || "submitting"} />}
    <div className="instc-layout" style={{ flexDirection: "column" }}>
      {!isOnline && (
        <div style={{ background: "rgba(248,113,113,0.15)", borderBottom: "1px solid rgba(248,113,113,0.3)", padding: "0.55rem 1.25rem", fontSize: "0.82rem", color: "#f87171", display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 500, flexShrink: 0 }}>
          <span>⚠</span> Connection lost — your code is saved locally and will submit when you reconnect.
        </div>
      )}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
      {/* Sidebar — problem list */}
      <div className="instc-sidebar">
        <div className="instc-sidebar-header">
          <div className="instc-brand">Vagmee</div>
          <div className="instc-campaign-name">{campaign.title}</div>
          {campaign.college_name && <div className="instc-college">{campaign.college_name}</div>}
          {!hideInternalTimer && timeLeft !== null && (
            <div className={`instc-timer${timeLeft < 300 ? " instc-timer--warn" : ""}`}>
              ⏱ {fmtTimeLeft(timeLeft)}
            </div>
          )}
        </div>
        <div className="instc-problem-list">
          {problems.map((p, i) => (
            <button
              key={i}
              className={`instc-problem-btn${current === i ? " active" : ""}${results[i]?.status === "accepted" ? " solved" : ""}`}
              onClick={() => setCurrent(i)}
            >
              <span className="instc-prob-num">{i + 1}</span>
              <span className="instc-prob-title">{p.title}</span>
              <span className="instc-prob-diff" style={{ color: diffColor[p.difficulty] || "#aaa" }}>
                {p.difficulty}
              </span>
            </button>
          ))}
        </div>
        {lastSavedAt && (
          <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.28)", textAlign: "center" }}>
            {!isOnline ? "⚡ saved offline" : "✓ saved"}
          </div>
        )}
      </div>

      {/* Main area */}
      <div className="instc-main">
        {/* Problem description */}
        <div className="instc-problem-panel">
          <div className="instc-problem-header">
            <span className="instc-prob-index">{current + 1}.</span>
            <span className="instc-prob-h-title">{problem.title}</span>
            <span className="instc-prob-h-diff" style={{ color: diffColor[problem.difficulty] || "#aaa" }}>
              {problem.difficulty}
            </span>
          </div>
          <div className="instc-io-notice">
            ⚠ Your program must read its input from standard input (stdin) and print the
            answer to standard output (stdout) — it is run and graded as a complete
            program, not called as a function. See the Input/Output examples below for
            the exact format.
          </div>
          <div className="instc-problem-desc">
            {problem.description || "No description provided."}
          </div>
          {problem.visible_testcases?.length > 0 && (
            <div className="instc-testcases">
              <div className="instc-tc-title">Examples (exact stdin / stdout format)</div>
              {problem.visible_testcases.slice(0, 3).map((tc, ti) => (
                <div key={ti} className="instc-tc">
                  <div><span className="instc-tc-label">Input:</span> <code>{tc.input || tc.stdin || "—"}</code></div>
                  <div><span className="instc-tc-label">Output:</span> <code>{tc.expected_output || tc.output || "—"}</code></div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Editor panel */}
        <div className="instc-editor-panel">
          <div className="instc-editor-toolbar">
            <select
              className="instc-lang-select"
              value={lang}
              onChange={(e) => changeLang(e.target.value)}
            >
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
            <div className="instc-toolbar-btns">
              <button className="instc-run-btn" onClick={runCode} disabled={running}>
                {running ? "Running…" : "▶ Run"}
              </button>
              {hideSubmitUntilLast && current < problems.length - 1 ? (
                <button
                  className="instc-run-btn"
                  onClick={() => setCurrent((c) => Math.min(c + 1, problems.length - 1))}
                  style={{ width: "auto", marginTop: 0, padding: "0.35rem 1rem" }}
                >
                  Next →
                </button>
              ) : (
                <button
                  className="instc-submit-all-btn"
                  onClick={handleSubmitClick}
                  disabled={submitting || !problems.length}
                  style={{ width: "auto", marginTop: 0, padding: "0.35rem 1rem" }}
                >
                  {submitting ? "Submitting…" : "Submit All"}
                </button>
              )}
              {submissionStatus === "failed" && !submitting && (
                <span style={{ fontSize: "0.7rem", color: "#f6c05a", alignSelf: "center" }}>
                  Retry ↑
                </span>
              )}
            </div>
          </div>
          <div className="instc-editor-wrap">
            <Editor
              height="100%"
              language={langObj.monacoLang}
              value={codes[current]}
              onChange={setCode}
              theme="vs-dark"
              options={{ fontSize: 13, minimap: { enabled: false }, scrollBeyondLastLine: false, wordWrap: "on" }}
            />
          </div>

          {/* Output */}
          <div className="instc-output">
            {!results[current] && (
              <p className="instc-output-placeholder">Run your code to see output here.</p>
            )}
            {results[current]?.status === "running" && (
              <p className="instc-output-running">⟳ Running…</p>
            )}
            {results[current] && results[current].status !== "running" && (
              <>
                <div className={`instc-verdict ${results[current].status}`}>
                  {results[current].status === "accepted" ? "✓ " : "✗ "}
                  {results[current].statusDesc}
                </div>
                {results[current].output && (
                  <pre className="instc-output-text">{results[current].output}</pre>
                )}
                {results[current].error && (
                  <pre className="instc-output-error">{results[current].error}</pre>
                )}
                {results[current].status === "error" && (
                  <p className="instc-rerun-hint">Fix your code and click ▶ Run to try again.</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
    {showMissedConfirm && (
      <div className="atest-confirm-overlay">
        <div className="atest-confirm-card">
          <h3>Unattempted problems</h3>
          <p>
            You have {missedCount} coding problem{missedCount === 1 ? "" : "s"} you haven't attempted yet, out of {problems.length}.
            Submit anyway? You can go back and attempt them first.
          </p>
          <div className="atest-confirm-actions">
            <button type="button" className="atest-confirm-btn is-secondary" onClick={() => setShowMissedConfirm(false)}>
              Go back
            </button>
            <button type="button" className="atest-confirm-btn is-primary" onClick={confirmSubmitAnyway}>
              Submit anyway
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ─── Screen-share + fullscreen gate (mirrors CompanyAssessmentRoute.jsx /
//     InstMcqRoute.jsx's own copies of this same card) ────────────────────────

function ProctoringGateCard({
  screenShareStatus, onShareScreen,
  fullscreenStatus, onEnterFullscreen, fullscreenAttempts, onSkipFullscreen,
}) {
  const screenShareDone = screenShareStatus === "active" || screenShareStatus === "unsupported";

  return (
    <div className="inst-mcq-error-card" style={{ textAlign: "center" }}>
      <div className="inst-mcq-error-icon">🖥️</div>
      <h2>Proctoring Setup</h2>
      <p>This assessment requires screen sharing and fullscreen mode so it can be proctored.</p>

      {!screenShareDone ? (
        <>
          <button className="inst-btn-primary" style={{ marginTop: "1.25rem" }} onClick={onShareScreen}>
            {screenShareStatus ? "Try Again" : "Share Your Screen"}
          </button>
          {screenShareStatus === "denied" && (
            <p style={{ marginTop: "0.75rem", color: "#f87171", fontSize: "0.85rem" }}>
              Screen sharing is required to continue. Please select a screen and click Share.
            </p>
          )}
          {screenShareStatus === "wrong_surface" && (
            <p style={{ marginTop: "0.75rem", color: "#f87171", fontSize: "0.85rem" }}>
              Please share your <strong>Entire Screen</strong>, not a specific window or tab.
            </p>
          )}
        </>
      ) : (
        <>
          {screenShareStatus === "unsupported" ? (
            <p style={{ color: "#f5a524", fontSize: "0.9rem" }}>
              Screen sharing isn't available on this device — continuing with camera and fullscreen proctoring only.
            </p>
          ) : (
            <p style={{ color: "#36c8a0", fontSize: "0.9rem" }}>✓ Screen sharing active</p>
          )}
          <button className="inst-btn-primary" style={{ marginTop: "0.5rem" }} onClick={onEnterFullscreen}>
            {fullscreenAttempts > 0 ? "Try Again" : "Enter Fullscreen"}
          </button>
          {fullscreenStatus === "failed" && (
            <>
              <p style={{ marginTop: "0.75rem", color: "#f87171", fontSize: "0.85rem" }}>
                Could not enter fullscreen mode.
              </p>
              {fullscreenAttempts > 0 && (
                <button className="link-button" style={{ marginTop: "0.25rem" }} onClick={onSkipFullscreen}>
                  Continue without fullscreen
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main route ───────────────────────────────────────────────────────────────

export default function InstCodingRoute() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loadingCampaign, setLoadingCampaign] = useState(true);
  const [campaignError, setCampaignError] = useState(null);
  const [currentUser, setCurrentUser] = useState(undefined);
  const [alreadyAttempted, setAlreadyAttempted] = useState(null);
  const [codingSubmitted, setCodingSubmitted] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraStatus, setCameraStatus] = useState(null); // null|"active"|"denied"
  // This standalone institutional coding campaign previously had no
  // screen-share/fullscreen gate or mid-test enforcement at all — only the
  // MCQ (and MCQ+Coding combined) route did. Mirrors that same gate + 2-strike
  // enforcement pattern so a coding-only campaign is proctored the same way.
  const [fullscreenStatus, setFullscreenStatus] = useState(null); // null|"active"|"failed"|"skipped"
  const [fullscreenAttempts, setFullscreenAttempts] = useState(0);
  const [screenStream, setScreenStream] = useState(null);
  const [screenShareStatus, setScreenShareStatus] = useState(null); // null|"active"|"denied"|"wrong_surface"|"unsupported"

  useEffect(() => {
    if (!isScreenShareSupported()) setScreenShareStatus("unsupported");
  }, []);

  const handleEnterFullscreen = async () => {
    try {
      await document.documentElement.requestFullscreen();
      setFullscreenStatus("active");
    } catch (_) {
      setFullscreenStatus("failed");
      setFullscreenAttempts((n) => n + 1);
    }
  };

  const handleShareScreen = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "monitor" },
        audio: false,
      });
      const track = stream.getVideoTracks()[0];
      const surface = track?.getSettings?.().displaySurface;
      if (surface && surface !== "monitor") {
        stream.getTracks().forEach((t) => t.stop());
        setScreenShareStatus("wrong_surface");
        return;
      }
      setScreenStream(stream);
      setScreenShareStatus("active");
    } catch (_) {
      setScreenShareStatus("denied");
    }
  };

  useEffect(() => {
    return () => { screenStream?.getTracks().forEach((t) => t.stop()); };
  }, [screenStream]);

  // 1. Load campaign + problems (no auth).
  //    Problems are cached in sessionStorage so the same candidate always
  //    gets the same set even on refresh — each new session gets a fresh random pick.
  useEffect(() => {
    async function load() {
      try {
        const cacheKey = `coding_problems_${campaignId}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          setCampaign(JSON.parse(cached));
          setLoadingCampaign(false);
          return;
        }
        const res = await fetch(`${API_BASE}/api/institutional/campaigns/${campaignId}/coding-problems`);
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || d.message || (res.status === 410 ? "This link is no longer active." : "Campaign not found."));
        }
        const data = await res.json();
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
        setCampaign(data);
      } catch (ex) {
        setCampaignError(ex.message);
      } finally {
        setLoadingCampaign(false);
      }
    }
    load();
  }, [campaignId]);

  // 2. Auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setCurrentUser(u));
    return unsub;
  }, []);

  // 3. Redirect to standard login if not authenticated
  useEffect(() => {
    if (currentUser === null) {
      navigate("/login", { state: { returnTo: `/inst/coding/${campaignId}` } });
    }
  }, [currentUser, campaignId, navigate]);

  // 4. Check if candidate already completed this campaign
  useEffect(() => {
    if (!currentUser) return;
    async function checkStatus() {
      try {
        const token = await getAuthToken();
        const res = await fetch(
          `${API_BASE}/api/institutional/campaigns/${campaignId}/my-status`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await res.json();
        setAlreadyAttempted(data.attempted === true);
      } catch {
        setAlreadyAttempted(false);
      }
    }
    checkStatus();
  }, [currentUser, campaignId]);

  // Proctoring — active for the full coding session (not just during a phase)
  const { warning, integrityScore, integrityLevel, signals, isFullscreenActive, isScreenShareLive } = useCampaignProctoring({
    enabled: !loadingCampaign && !!currentUser && !codingSubmitted,
    campaignId,
    mediaStream: cameraStream,
    screenStream,
  });

  // Live-state-driven proctoring enforcement — ONE combined overlay covering
  // both fullscreen and screen-share, not two independent counter-threshold
  // overlays (which could collide: clicking the browser's native "Stop
  // sharing" control also forces an exit from fullscreen as a browser-level
  // side effect, so a single candidate action could trip both at once, and
  // only the top-stacked overlay was ever visible/clickable).
  //
  // Deliberately no auto-submit-after-N-violations (there used to be one).
  // Resolving screen-share opens a native browser picker, which can itself
  // momentarily kick the candidate back out of fullscreen as a side
  // effect — so legitimately fixing ONE violation could coincidentally trip
  // the OTHER a moment later, registering as a second "strike" and silently
  // force-submitting the candidate even though they'd done nothing wrong
  // beyond the original, already-being-corrected violation. The overlay
  // itself is the enforcement; every violation still increments
  // fullscreenExitCount / screenShareStoppedCount into the integrity score.
  const fullscreenBroken = fullscreenStatus === "active" && !isFullscreenActive;
  const screenShareBroken = screenShareStatus === "active" && !isScreenShareLive;
  const midTestBlocked = fullscreenBroken || screenShareBroken;

  const handleReturnToFullscreen = async () => {
    try { await document.documentElement.requestFullscreen(); } catch (_) {}
  };

  const handleSubmitAll = async (submissions) => {
    const token = await getAuthToken();
    if (!token) throw new Error("Authentication failed — please refresh and try again.");
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), 60000);
    const payload = JSON.stringify({ submissions });
    try {
      const res = await fetch(`${API_BASE}/api/institutional/campaigns/${campaignId}/coding-submit`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    payload,
        signal:  controller.signal,
        // Browsers reject keepalive payloads above 64 KiB. Larger assessments
        // use the existing saved draft/retry flow if navigation interrupts them.
        keepalive: new Blob([payload]).size < 60000,
      });
      clearTimeout(tid);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || d.message || `Server error ${res.status}`);
      }
      const receipt = await res.json();
      setCodingSubmitted(true); // stop proctoring after durable acknowledgment
      return receipt;
    } catch (err) {
      clearTimeout(tid);
      throw err; // propagate so CodingTest retry logic fires
    }
  };

  if (loadingCampaign || currentUser === undefined || currentUser === null || alreadyAttempted === null) {
    return (
      <div className="inst-mcq-page">
        <CampaignTopBar />
        <div className="inst-mcq-loading">Loading coding test…</div>
      </div>
    );
  }

  if (campaignError) {
    return (
      <div className="inst-mcq-page">
        <CampaignTopBar />
        <div className="inst-mcq-error-card">
          <div className="inst-mcq-error-icon">⚠️</div>
          <h2>Test Unavailable</h2>
          <p>{campaignError}</p>
        </div>
      </div>
    );
  }

  if (alreadyAttempted) {
    return (
      <div className="inst-mcq-page">
        <CampaignTopBar />
        <div className="inst-already-done">
          <div className="inst-already-done__icon">✓</div>
          <h2>Assessment Already Submitted</h2>
          <p>You have already completed this assessment. Each link can only be attempted once.</p>
          <p className="inst-already-done__sub">If you believe this is an error, please contact your coordinator.</p>
        </div>
      </div>
    );
  }

  if (cameraStatus === "denied") {
    return (
      <div className="inst-mcq-page">
        <CampaignTopBar />
        <div className="inst-mcq-error-card" style={{ textAlign: "center" }}>
          <div className="inst-mcq-error-icon">📷</div>
          <h2>Camera Access Required</h2>
          <p>
            This assessment requires camera access for proctoring. Please allow
            camera access in your browser settings, then reload the page.
          </p>
          <button
            className="inst-btn-primary"
            style={{ marginTop: "1.25rem" }}
            onClick={() => window.location.reload()}
          >
            Reload &amp; Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!campaign.problems || campaign.problems.length === 0) {
    return (
      <div className="inst-mcq-page">
        <CampaignTopBar />
        <div className="inst-mcq-error-card">
          <div className="inst-mcq-error-icon">📭</div>
          <h2>No Problems Available</h2>
          <p>The problem pool for this test is empty. Please contact your coordinator.</p>
        </div>
      </div>
    );
  }

  const proctoringGateComplete =
    (screenShareStatus === "active" || screenShareStatus === "unsupported") &&
    (fullscreenStatus === "active" || fullscreenStatus === "skipped");

  return (
    <div className="inst-topbar-offset" style={{ position: "relative" }}>
      <CampaignTopBar integrityScore={integrityScore} integrityLevel={integrityLevel} />
      {warning && (
        <div className="inst-proctor-banner">
          <span className="inst-proctor-icon">⚠</span>
          {warning}
        </div>
      )}
      {!codingSubmitted && <SelfViewCamera onStream={setCameraStream} onStatus={setCameraStatus} />}
      {proctoringGateComplete ? (
        <CodingTest
          campaign={campaign}
          problems={campaign.problems}
          onSubmitAll={handleSubmitAll}
          campaignId={campaign.campaign_id || campaignId}
        />
      ) : (
        <ProctoringGateCard
          screenShareStatus={screenShareStatus}
          onShareScreen={handleShareScreen}
          fullscreenStatus={fullscreenStatus}
          fullscreenAttempts={fullscreenAttempts}
          onEnterFullscreen={handleEnterFullscreen}
          onSkipFullscreen={() => setFullscreenStatus("skipped")}
        />
      )}
      {midTestBlocked && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "grid", placeItems: "center", background: "rgba(5,3,15,0.85)", backdropFilter: "blur(6px)" }}>
          <div className="inst-mcq-error-card" style={{ textAlign: "center" }}>
            <div className="inst-mcq-error-icon">⚠️</div>
            <h2>Proctoring Requirement Not Met</h2>
            {fullscreenBroken && <p>You exited fullscreen mode.</p>}
            {screenShareBroken && <p>You stopped sharing your screen.</p>}
            <p style={{ color: "#f87171", fontSize: "0.85rem" }}>
              This has been recorded as a proctoring violation. If this happens again, your test will be submitted automatically.
            </p>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "1.25rem", flexWrap: "wrap" }}>
              {fullscreenBroken && (
                <button className="inst-btn-primary" onClick={handleReturnToFullscreen}>Return to Fullscreen</button>
              )}
              {screenShareBroken && (
                <button className="inst-btn-primary" onClick={handleShareScreen}>Resume Screen Sharing</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
